import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAdmin } from '../context/AdminContext';
import { ModalCobroCliente } from '../components/ModalCobroCliente';

import { API } from '../config/api';
import { apiFetch } from '../config/http';

// ── Paleta corporativa Infra Design ────────────────────────────────────────────
const C = {
  navy:      '#012E40',
  navy2:     '#024055',
  gris:      '#545454',
  grisBg:    '#f1f1f1',
  grisClaro: '#FAFAFA',
  verde:     '#87B749',
  verdeOsc:  '#3B6D11',
  verdeBg:   '#EAF3DE',
  azulTx:    '#185FA5',
  azulBg:    '#D4E9F7',
  amberTx:   '#854F0B',
  amberBg:   '#FAEEDA',
  rojo:      '#A32D2D',
  borde:     '#e0e0e0',
};

const MESES_LARGOS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const MESES_CORTOS = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const MONEDAS = ['Colones','Dólares'];
const SIM = { 'Colones': '₡', 'Dólares': '$' };

const inputStyle = {
  padding: '8px 12px', border: '1px solid ' + C.borde, borderRadius: '8px',
  fontSize: '13px', outline: 'none', boxSizing: 'border-box',
  fontFamily: 'Segoe UI, sans-serif', background: '#fff'
};

// ── Helpers formato ────────────────────────────────────────────────────────────
const fmtInt = (n) => n == null ? '—' : Math.round(n).toLocaleString('es-CR');
const fmtMonto = (n, moneda) => {
  if (n == null || n === '' || isNaN(n)) return '—';
  const v = Number(n);
  if (v === 0) return '—';
  return SIM[moneda] + v.toLocaleString('es-CR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
};
const fmtFull = (n, moneda) => {
  if (n == null || isNaN(n)) return '—';
  return SIM[moneda] + Number(n).toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};
const mesDeISO = (isoStr) => {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return isNaN(d.getTime()) ? null : d.getUTCMonth(); // 0..11
};

// Monto a mostrar en la matriz. Si la linea ya esta enlazada a un cobro y ese cobro
// tiene hitos para su linea de oferta, usamos el monto REAL facturado (monto_facturado,
// suma de hitos). Las pendientes muestran la proyeccion (pf.monto). Asi una factura con
// monto editado (p.ej. varias visitas en el mes) se refleja en la celda en vez del
// monto proyectado de una sola.
const montoEfectivo = (l) =>
  (l && l.cobro_id && l.monto_facturado != null)
    ? Number(l.monto_facturado)
    : (Number(l?.monto) || 0);

// Clave de agrupación por fila
const filaKey = (proyecto_id, linea_oferta_id) =>
  `${proyecto_id}||${linea_oferta_id ?? 'sin-item'}`;

// Clave de celda (proyecto + linea + mes + moneda)
const celdaKey = (proyecto_id, linea_oferta_id, mes, moneda) =>
  `${filaKey(proyecto_id, linea_oferta_id)}||${mes}||${moneda}`;

// Estilo de celda por estado
const estiloEstado = (estado) => {
  if (estado === 'Facturado') return { background: C.verdeBg, color: C.verdeOsc, borderColor: C.verdeOsc };
  if (estado === 'Cobrado')   return { background: C.azulBg,  color: C.azulTx,   borderColor: C.azulTx };
  return { background: C.grisBg, color: C.gris, borderColor: '#d6d6d6' };
};

// ¿Una celda tiene al menos una línea bloqueada (Facturada/Cobrada)?
const celdaBloqueada = (lineas) => {
  if (!lineas || lineas.length === 0) return false;
  return lineas.some(l =>
    l.estado_calculado === 'Facturado' ||
    l.estado_calculado === 'Cobrado' ||
    (l.cobro_id && l.numero_factura_cliente)
  );
};

// Helper: ISO del día 1 de un (anio, mes-0-index)
const fechaDia1 = (anio, mes) =>
  new Date(Date.UTC(anio, mes, 1)).toISOString().slice(0, 10);

// ── Modal edición de celda ─────────────────────────────────────────────────────
function ModalCelda({ proyecto, descripcion, mes, moneda, lineas, onCerrar, onGuardado, isAdmin, onSolicitar }) {
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [formNueva, setFormNueva] = useState({ monto: '', estado_manual: '' });

  const crearLinea = async () => {
    setError(null);
    const monto = parseFloat(formNueva.monto);
    if (!monto || monto <= 0) { setError('Monto inválido'); return; }
    // La moneda se define en la oferta y nunca debe diferir aguas abajo.
    // Si la celda de la matriz pertenece a una moneda distinta a la del
    // contrato (oferta), bloqueamos la creacion para evitar registros
    // huerfanos en la moneda equivocada.
    if (proyecto.moneda_contrato && proyecto.moneda_contrato !== moneda) {
      setError(`Este proyecto factura en ${proyecto.moneda_contrato} segun la oferta. No se puede crear una línea de proyección en ${moneda}.`);
      return;
    }
    setGuardando(true);
    try {
      const fecha = new Date(Date.UTC(new Date().getUTCFullYear(), mes, 1)).toISOString().slice(0,10);
      await axios.post(`${API}/proyeccion-facturacion`, {
        proyecto_id: proyecto.proyecto_id,
        linea_oferta_id: proyecto.linea_oferta_id || null,
        descripcion: descripcion,
        item_numero: proyecto.item_numero || null,
        facturar_con: proyecto.facturar_con,
        moneda: proyecto.moneda_contrato || moneda,
        monto: monto,
        fecha_proyectada: fecha,
        estado_manual: formNueva.estado_manual || null,
      });
      setFormNueva({ monto: '', estado_manual: '' });
      onGuardado();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const actualizarLinea = async (linea, cambios) => {
    setError(null);
    setGuardando(true);
    try {
      await axios.put(`${API}/proyeccion-facturacion?id=${linea.id}`, cambios);
      onGuardado();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  const eliminarLinea = async (linea) => {
    if (!window.confirm(`¿Eliminar esta línea de ${fmtFull(linea.monto, linea.moneda)}?`)) return;
    setError(null);
    setGuardando(true);
    try {
      await axios.delete(`${API}/proyeccion-facturacion?id=${linea.id}&hard=true`);
      onGuardado();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
         onClick={onCerrar}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '24px', width: '520px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
           onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div style={{ borderBottom: '1px solid ' + C.borde, paddingBottom: '12px', marginBottom: '16px' }}>
          <div style={{ fontSize: '11px', color: C.gris, textTransform: 'uppercase', letterSpacing: '1px', fontWeight: '600' }}>
            {proyecto.proyecto_id} · {proyecto.facturar_con}
          </div>
          <div style={{ fontSize: '15px', fontWeight: '600', color: C.navy, marginTop: '4px' }}>
            {descripcion}
          </div>
          <div style={{ fontSize: '13px', color: C.gris, marginTop: '2px' }}>
            {MESES_LARGOS[mes]} {new Date().getUTCFullYear()} · {moneda}
          </div>
        </div>

        {error && (
          <div style={{ background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: '8px', padding: '8px 12px', marginBottom: '12px', fontSize: '12px', color: C.rojo }}>
            {error}
          </div>
        )}

        {/* Líneas existentes */}
        {lineas.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', color: C.gris, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Líneas existentes ({lineas.length})
            </div>
            {lineas.map(l => (
              <LineaEditable key={l.id} linea={l} moneda={moneda} isAdmin={isAdmin}
                onActualizar={(c) => actualizarLinea(l, c)}
                onEliminar={() => eliminarLinea(l)}
                onSolicitar={onSolicitar ? () => onSolicitar(l) : null}
                guardando={guardando} />
            ))}
          </div>
        )}

        {/* Agregar nueva */}
        {isAdmin && (
          <div style={{ background: C.grisClaro, border: '1px solid ' + C.borde, borderRadius: '10px', padding: '12px' }}>
            <div style={{ fontSize: '11px', color: C.gris, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>
              Agregar nueva línea
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '10px' }}>
              <div>
                <label style={{ fontSize: '11px', color: C.gris, display: 'block', marginBottom: '3px' }}>Monto ({moneda})</label>
                <input type="number" step="0.01" value={formNueva.monto}
                  onChange={e => setFormNueva({ ...formNueva, monto: e.target.value })}
                  style={{ ...inputStyle, width: '100%' }} placeholder="0.00" />
              </div>
              <div>
                <label style={{ fontSize: '11px', color: C.gris, display: 'block', marginBottom: '3px' }}>Estado manual</label>
                <select value={formNueva.estado_manual}
                  onChange={e => setFormNueva({ ...formNueva, estado_manual: e.target.value })}
                  style={{ ...inputStyle, width: '100%' }}>
                  <option value="">Auto (Pendiente)</option>
                  <option value="Facturado">Facturado</option>
                  <option value="Cobrado">Cobrado</option>
                </select>
              </div>
            </div>
            <button onClick={crearLinea} disabled={guardando}
              style={{ background: C.verde, color: '#fff', border: 'none', padding: '8px 20px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: guardando ? 'default' : 'pointer', opacity: guardando ? 0.6 : 1 }}>
              {guardando ? 'Guardando…' : '+ Agregar'}
            </button>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '16px' }}>
          <button onClick={onCerrar} style={{ padding: '8px 20px', border: '1px solid ' + C.borde, borderRadius: '8px', background: '#fff', cursor: 'pointer', fontSize: '13px' }}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Sub-componente línea editable dentro del modal ─────────────────────────────
function LineaEditable({ linea, moneda, isAdmin, onActualizar, onEliminar, guardando, onSolicitar }) {
  const [editando, setEditando] = useState(false);
  const [monto, setMonto] = useState(linea.monto);
  const [estado, setEstado] = useState(linea.estado_manual || '');

  const guardar = () => {
    const cambios = {};
    if (parseFloat(monto) !== parseFloat(linea.monto)) cambios.monto = parseFloat(monto);
    if ((estado || null) !== (linea.estado_manual || null)) cambios.estado_manual = estado || null;
    if (Object.keys(cambios).length === 0) { setEditando(false); return; }
    onActualizar(cambios);
    setEditando(false);
  };

  const est = estiloEstado(linea.estado_calculado);

  if (editando) {
    return (
      <div style={{ background: '#fffceb', border: '1px solid #ffd670', borderRadius: '8px', padding: '10px', marginBottom: '8px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '8px', alignItems: 'end' }}>
          <div>
            <label style={{ fontSize: '10px', color: C.gris, display: 'block' }}>Monto</label>
            <input type="number" step="0.01" value={monto} onChange={e => setMonto(e.target.value)} style={{ ...inputStyle, width: '100%', padding: '6px 10px' }} />
          </div>
          <div>
            <label style={{ fontSize: '10px', color: C.gris, display: 'block' }}>Estado manual</label>
            <select value={estado} onChange={e => setEstado(e.target.value)} style={{ ...inputStyle, width: '100%', padding: '6px 10px' }}>
              <option value="">Auto</option>
              <option value="Facturado">Facturado</option>
              <option value="Cobrado">Cobrado</option>
            </select>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            <button onClick={guardar} disabled={guardando} style={{ background: C.verde, color: '#fff', border: 'none', padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>✓</button>
            <button onClick={() => setEditando(false)} style={{ background: '#fff', border: '1px solid ' + C.borde, padding: '6px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' }}>✕</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ border: '1px solid ' + C.borde, borderRadius: '8px', padding: '10px 12px', marginBottom: '8px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <div>
        <div style={{ fontSize: '15px', fontWeight: '600', color: C.navy }}>
          {fmtFull(montoEfectivo(linea), moneda)}
        </div>
        {linea.cobro_id && linea.monto_facturado != null && Number(linea.monto_facturado) !== Number(linea.monto) && (
          <div style={{ fontSize: '10.5px', color: C.gris, marginTop: '2px' }}>
            Proyectado: {fmtFull(linea.monto, moneda)}
          </div>
        )}
        <span style={{ display: 'inline-block', marginTop: '4px', padding: '2px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '500', background: est.background, color: est.color, border: '1px solid ' + est.borderColor }}>
          {linea.estado_calculado}{linea.estado_manual ? ' (manual)' : ''}
        </span>
        {linea.cobro_id && (
          <span style={{ marginLeft: '8px', fontSize: '11px', color: C.gris }}>
            Cobro #{linea.cobro_id}{linea.numero_factura_cliente ? ` · FE ${linea.numero_factura_cliente}` : ''}
          </span>
        )}
      </div>
      {isAdmin && (
        <div style={{ display: 'flex', gap: '6px' }}>
          {onSolicitar && !linea.cobro_id && linea.estado_calculado !== 'Facturado' && linea.estado_calculado !== 'Cobrado' && (
            <button onClick={onSolicitar} style={{ background: C.navy, color: '#fff', border: 'none', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px' }}>Solicitar factura</button>
          )}
          <button onClick={() => setEditando(true)} style={{ background: '#fff', border: '1px solid ' + C.borde, padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: C.navy }}>Editar</button>
          <button onClick={onEliminar} style={{ background: '#fff', border: '1px solid #F7C1C1', padding: '5px 10px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', color: C.rojo }}>Eliminar</button>
        </div>
      )}
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────────────────────
export default function Proyeccion() {
  const isAdmin = useAdmin();

  const [anio, setAnio] = useState(new Date().getUTCFullYear());
  // Mes actual real (no la seleccion del usuario): para marcar pasado / actual / futuro
  const ANIO_ACTUAL = new Date().getFullYear();
  const MES_ACTUAL  = new Date().getMonth();
  const esPasado = (m) => anio < ANIO_ACTUAL || (anio === ANIO_ACTUAL && m < MES_ACTUAL);
  const esActual = (m) => anio === ANIO_ACTUAL && m === MES_ACTUAL;
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState({ items: [], lineas: [], totales: {} });

  const [filtro, setFiltro] = useState('');
  const [filtroEmpresa, setFiltroEmpresa] = useState('');
  const [ocultarCerradas, setOcultarCerradas] = useState(false); // ocultar filas 100% Cobradas
  const [mostrarOcultas,  setMostrarOcultas]  = useState(false); // revelar filas marcadas manualmente como ocultas

  const [celdaActiva, setCeldaActiva] = useState(null); // { proyecto, descripcion, mes, moneda, lineas }

  // Fase B (#4): solicitar factura desde la matriz reutilizando ModalCobroCliente.
  const [solicitar, setSolicitar] = useState(null);              // { lineaId, initialForm }
  const [listas, setListas]       = useState({ clientes: [], proyectos: [] });

  // ── Drag & Drop (HTML5 nativo) ──────────────────────────────────────────────
  // dragFrom = { filaKey, fromMes, moneda } — celda origen en curso
  // dragOverKey = celda sobre la que se está haciendo hover (para highlight)
  const [dragFrom, setDragFrom]     = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [shiftMsg, setShiftMsg]     = useState(null); // feedback breve de shift
  const [shiftError, setShiftError] = useState(null);

  // Asignacion bulk de filas huerfanas a un item de oferta
  const [asignandoProyecto, setAsignandoProyecto] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/proyeccion-facturacion?action=matriz&anio=${anio}`);
      setData({
        items:   r.data.items   || [],
        lineas:  r.data.lineas  || [],
        totales: r.data.totales || {}
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setCargando(false);
    }
  }, [anio]);

  useEffect(() => { cargar(); }, [cargar]);

  // Listas para el modal de cobro (Fase B). Se cargan una vez.
  useEffect(() => {
    (async () => {
      try {
        const [rCl, rP] = await Promise.all([
          axios.get(`${API}/clientes`),
          axios.get(`${API}/proyectos`)
        ]);
        setListas({ clientes: rCl.data || [], proyectos: rP.data || [] });
      } catch (e) { /* el modal podra abrirse igual; selects vacios como fallback */ }
    })();
  }, []);

  // Abre el modal de cobro prellenado desde una linea proyectada pendiente.
  const abrirSolicitar = (linea) => {
    const proy = listas.proyectos.find(p => p.id_proyecto === linea.proyecto_id);
    const hoy  = new Date().toISOString().split('T')[0];
    setCeldaActiva(null);
    setSolicitar({
      lineaId: linea.id,
      initialForm: {
        cliente_id:             proy?.cliente_id || '',
        numero_proyecto:        linea.proyecto_id,
        numero_factura_cliente: '',
        descripcion:            '',
        moneda:                 linea.moneda || 'Dólares',
        facturar_con:           linea.facturar_con || 'JyA Conusa',
        fecha_emision:          hoy,
        fecha_vencimiento:      '',
        estado:                 'Pendiente',
        notas:                  '',
        hitos:                  [],
        // v42: contexto de la celda clicada para premarcar el hito exacto en el modal.
        // hito_id puede ser null en filas legacy -> el modal cae al fallback nivel-linea.
        targetHitoId:           linea.hito_id ?? null,
        targetLineaOfertaId:    linea.linea_oferta_id ?? null
      }
    });
  };

  // Asignar bulk: pone linea_oferta_id en todas las huerfanas de un proyecto.
  // Llamado desde el dropdown inline en la fila "Sin item asignado".
  const asignarHuerfanas = async (proyectoCodigo, lineaOfertaId, huerfanas) => {
    setAsignandoProyecto(proyectoCodigo);
    try {
      const resultados = await Promise.all(huerfanas.map(h =>
        apiFetch(`${API}/proyeccion-facturacion?id=${h.id}`, {
          method:  'PUT',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({ linea_oferta_id: lineaOfertaId })
        }).then(r => r.ok)
      ));
      const okCount = resultados.filter(Boolean).length;
      if (okCount !== huerfanas.length) {
        alert(`Asignadas ${okCount} de ${huerfanas.length} filas. Recargando para ver el resultado.`);
      }
      await cargar();
    } catch (e) {
      alert('Error al asignar huerfanas: ' + e.message);
    } finally {
      setAsignandoProyecto(null);
    }
  };

  // Construcción de filas: items + filas "Sin item" por proyecto con líneas huérfanas
  const filas = useMemo(() => {
    const out = [];
    const proyectosConItems = new Set(data.items.map(i => i.proyecto_id));
    const proyectosConHuerfanas = new Set();
    const huerfanasPorProyecto = {};

    // Detectar líneas huérfanas (proyecto tiene oferta pero la línea no tiene linea_oferta_id)
    for (const l of data.lineas) {
      if (!l.linea_oferta_id && proyectosConItems.has(l.proyecto_id)) {
        proyectosConHuerfanas.add(l.proyecto_id);
        if (!huerfanasPorProyecto[l.proyecto_id]) huerfanasPorProyecto[l.proyecto_id] = [];
        huerfanasPorProyecto[l.proyecto_id].push(l);
      }
    }

    // Agrupar items por proyecto_id (el orden ya viene de BD)
    const itemsPorProy = {};
    for (const it of data.items) {
      if (!itemsPorProy[it.proyecto_id]) itemsPorProy[it.proyecto_id] = [];
      itemsPorProy[it.proyecto_id].push(it);
    }

    for (const proyecto_id of Object.keys(itemsPorProy).sort()) {
      for (const it of itemsPorProy[proyecto_id]) {
        out.push({
          tipo: 'item',
          proyecto_id:       it.proyecto_id,
          proyecto_nombre:   it.proyecto_nombre,
          proyecto_estado:   it.proyecto_estado,
          cliente_nombre:    it.cliente_nombre,
          linea_oferta_id:   it.linea_oferta_id,
          item_numero:       it.item_numero,
          descripcion:       it.item_descripcion_corta || '(sin descripción)',
          facturar_con:      it.oferta_facturar_con,
          moneda_contrato:   it.oferta_moneda,
          monto_contrato:    it.item_monto_contrato,
          oculto:            !!it.oculto,
        });
      }
      if (proyectosConHuerfanas.has(proyecto_id)) {
        const lh = huerfanasPorProyecto[proyecto_id];
        out.push({
          tipo: 'sin-item',
          proyecto_id:      proyecto_id,
          proyecto_nombre:  lh[0].proyecto_nombre,
          proyecto_estado:  lh[0].proyecto_estado,
          cliente_nombre:   lh[0].cliente_nombre,
          linea_oferta_id:  null,
          item_numero:      null,
          descripcion:      '⚠ Sin item asignado',
          facturar_con:     lh[0].facturar_con,
          moneda_contrato:  null,
          monto_contrato:   null,
          huerfanas:        lh,
          itemsDeProyecto:  itemsPorProy[proyecto_id] || [],
        });
      }
    }
    return out;
  }, [data]);

  // Index de celdas → líneas (fast lookup)
  const celdasIndex = useMemo(() => {
    const idx = {};
    for (const l of data.lineas) {
      const mes = mesDeISO(l.fecha_proyectada);
      if (mes == null) continue;
      const k = celdaKey(l.proyecto_id, l.linea_oferta_id, mes, l.moneda);
      if (!idx[k]) idx[k] = [];
      idx[k].push(l);
    }
    return idx;
  }, [data.lineas]);

  // Lineas agrupadas por fila (proyecto + item) para evaluar el estado de la fila.
  const lineasPorFila = useMemo(() => {
    const m = {};
    for (const l of data.lineas) {
      const k = filaKey(l.proyecto_id, l.linea_oferta_id);
      if (!m[k]) m[k] = [];
      m[k].push(l);
    }
    return m;
  }, [data.lineas]);

  // Una fila esta "cerrada" si tiene proyeccion y TODA esta en estado Cobrado.
  // Las filas sin proyectar tienen 0 lineas -> nunca cuentan como cerradas, asi que
  // el toggle nunca esconde trabajo en curso (solo lo cobrado del ano en vista).
  const filaCerrada = useCallback((f) => {
    const ls = lineasPorFila[filaKey(f.proyecto_id, f.linea_oferta_id)] || [];
    return ls.length > 0 && ls.every(l => l.estado_calculado === 'Cobrado');
  }, [lineasPorFila]);

  // Cuantas filas cerradas hay (indicador junto al toggle).
  const nCerradas = useMemo(() => filas.filter(filaCerrada).length, [filas, filaCerrada]);
  const nOcultas  = useMemo(() => filas.filter(f => f.oculto).length, [filas]);

  // Filtrado visual
  const filasVisibles = useMemo(() => {
    return filas.filter(f => {
      if (!mostrarOcultas && f.oculto) return false;
      if (ocultarCerradas && filaCerrada(f)) return false;
      if (filtroEmpresa && f.facturar_con !== filtroEmpresa) return false;
      if (filtro) {
        const t = filtro.toLowerCase();
        const hay = (f.proyecto_id + ' ' + f.proyecto_nombre + ' ' + f.descripcion + ' ' + (f.cliente_nombre || '')).toLowerCase();
        if (!hay.includes(t)) return false;
      }
      return true;
    });
  }, [filas, filtro, filtroEmpresa, ocultarCerradas, filaCerrada, mostrarOcultas]);

  // Ocultar/mostrar una fila (item de oferta) en la matriz. Marca manual en BD.
  const toggleOcultarFila = async (fila) => {
    if (!fila.linea_oferta_id) return;
    try {
      await apiFetch(`${API}/proyeccion-facturacion?action=ocultar-item`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ linea_oferta_id: fila.linea_oferta_id, ocultar: !fila.oculto })
      });
      await cargar();
    } catch (e) { alert('No se pudo cambiar la visibilidad: ' + e.message); }
  };

  const abrirCelda = (fila, mes, moneda) => {
    if (!isAdmin) return;
    const k = celdaKey(fila.proyecto_id, fila.linea_oferta_id, mes, moneda);
    const lineas = celdasIndex[k] || [];
    setCeldaActiva({ proyecto: fila, descripcion: fila.descripcion, mes, moneda, lineas });
  };

  // Handlers D&D — cada celda clickeable con líneas no bloqueadas es draggable;
  // drop target válido = celda de la misma fila × moneda (distinta del origen).
  const handleDragStart = (fila, mes, moneda, e) => {
    if (!isAdmin) { e.preventDefault(); return; }
    const fk = filaKey(fila.proyecto_id, fila.linea_oferta_id);
    setDragFrom({ fk, fromMes: mes, moneda, fila });
    // Payload opcional (para arrastre entre tabs, no lo usamos)
    try { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', `${fk}|${mes}|${moneda}`); } catch (_) {}
  };

  const handleDragEnd = () => {
    setDragFrom(null);
    setDragOverKey(null);
  };

  const handleDragOver = (fila, mes, moneda, e) => {
    if (!dragFrom) return;
    const fk = filaKey(fila.proyecto_id, fila.linea_oferta_id);
    const valido = (fk === dragFrom.fk) && (moneda === dragFrom.moneda) && (mes !== dragFrom.fromMes);
    if (!valido) return;
    e.preventDefault(); // habilita el drop
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) {}
    const k = celdaKey(fila.proyecto_id, fila.linea_oferta_id, mes, moneda);
    if (dragOverKey !== k) setDragOverKey(k);
  };

  const handleDrop = async (fila, mes, moneda, e) => {
    if (!dragFrom) return;
    e.preventDefault();
    const fk = filaKey(fila.proyecto_id, fila.linea_oferta_id);
    if (fk !== dragFrom.fk || moneda !== dragFrom.moneda || mes === dragFrom.fromMes) {
      handleDragEnd();
      return;
    }
    const delta = mes - dragFrom.fromMes;
    const fecha_origen = fechaDia1(anio, dragFrom.fromMes);

    setShiftMsg(`Moviendo ${MESES_CORTOS[dragFrom.fromMes]} → ${MESES_CORTOS[mes]} (Δ ${delta > 0 ? '+' : ''}${delta}) …`);
    setShiftError(null);
    try {
      const r = await axios.post(`${API}/proyeccion-facturacion?action=shift-row`, {
        proyecto_id:      fila.proyecto_id,
        linea_oferta_id:  fila.linea_oferta_id,
        moneda:           moneda,
        fecha_origen:     fecha_origen,
        delta_meses:      delta,
      });
      setShiftMsg(`${r.data.afectadas} línea(s) movidas`);
      setTimeout(() => setShiftMsg(null), 2500);
      await cargar();
    } catch (err) {
      setShiftMsg(null);
      const msg = err.response?.data?.error || err.message;
      setShiftError(msg);
      setTimeout(() => setShiftError(null), 5000);
    } finally {
      handleDragEnd();
    }
  };

  // Totales mensuales por moneda (ya vienen del API)
  const totalesMes = (mes, moneda) => {
    const key = `${anio}-${String(mes+1).padStart(2,'0')}`;
    return data.totales[key]?.[moneda]?.proyectado || 0;
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 24px', fontFamily: 'Segoe UI, sans-serif', minHeight: '100vh' }}>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '22px', color: C.navy, margin: 0, fontWeight: '700' }}>Proyección de Facturación</h1>
          <p style={{ fontSize: '13px', color: C.gris, margin: '4px 0 0' }}>
            Matriz de cobros proyectados · {anio} · {filasVisibles.length} filas · {isAdmin ? 'Modo edición · clic para editar · arrastrar para mover en cascada' : 'Solo lectura'}
          </p>
        </div>
        {!isAdmin && (
          <div style={{ background: C.amberBg, color: C.amberTx, padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '500' }}>
            Vista de solo lectura
          </div>
        )}
        {isAdmin && shiftMsg && (
          <div style={{ background: C.verdeBg, color: C.verdeOsc, padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', border: '1px solid ' + C.verdeOsc }}>
            {shiftMsg}
          </div>
        )}
        {isAdmin && shiftError && (
          <div style={{ background: '#FCEBEB', color: C.rojo, padding: '6px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: '500', border: '1px solid #F7C1C1' }}>
            {shiftError}
          </div>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
        <input type="text" placeholder="Buscar proyecto, cliente, descripción..." value={filtro} onChange={e => setFiltro(e.target.value)}
               style={{ ...inputStyle, width: '260px' }} />
        <select value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)} style={{ ...inputStyle, width: '160px' }}>
          <option value="">Todas las empresas</option>
          <option>JyA Conusa</option>
          <option>Cuenta Personal (JCNS)</option>
        </select>
        <select value={anio} onChange={e => setAnio(parseInt(e.target.value))} style={{ ...inputStyle, width: '100px' }}>
          {[anio-1, anio, anio+1].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <label title="Oculta las filas cuya proyeccion de este ano esta 100% cobrada"
               style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.gris, cursor: 'pointer', userSelect: 'none' }}>
          <input type="checkbox" checked={ocultarCerradas} onChange={e => setOcultarCerradas(e.target.checked)} />
          Ocultar cerradas{nCerradas ? ` (${nCerradas})` : ''}
        </label>
        {nOcultas > 0 && (
          <label title="Revela las filas marcadas manualmente como ocultas para poder restaurarlas"
                 style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: C.gris, cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={mostrarOcultas} onChange={e => setMostrarOcultas(e.target.checked)} />
            Mostrar ocultas ({nOcultas})
          </label>
        )}

        {/* Leyenda */}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '12px', alignItems: 'center', fontSize: '11px', color: C.gris }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: C.grisBg, border: '1px solid #ccc', borderRadius: '2px' }}/>Pendiente
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: C.verdeBg, border: '1px solid ' + C.verdeOsc, borderRadius: '2px' }}/>Facturado
          </span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ display: 'inline-block', width: '12px', height: '12px', background: C.azulBg, border: '1px solid ' + C.azulTx, borderRadius: '2px' }}/>Cobrado
          </span>
        </div>
      </div>

      {/* Estados */}
      {cargando && filas.length === 0 && (
        <div style={{ padding: '40px', textAlign: 'center', color: C.gris, fontSize: '13px' }}>Cargando matriz…</div>
      )}
      {error && (
        <div style={{ background: '#FCEBEB', border: '1px solid #F7C1C1', borderRadius: '8px', padding: '12px', color: C.rojo, fontSize: '13px', marginBottom: '12px' }}>
          Error: {error}
        </div>
      )}

      {/* Tabla matriz — el contenedor se mantiene montado durante las recargas
          (condicion por datos, NO por `cargando`) para no perder la posicion del
          scroll. Antes se desmontaba al actualizar y la pantalla saltaba arriba.
          Durante la recarga se atenua levemente como feedback. */}
      {!error && (filas.length > 0 || !cargando) && (
        <div style={{ position: 'relative', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 200px)', border: '1px solid ' + C.borde, borderRadius: '10px', background: '#fff', opacity: cargando ? 0.6 : 1, transition: 'opacity 0.15s' }}>
          <table style={{ borderCollapse: 'separate', borderSpacing: 0, fontSize: '11px', whiteSpace: 'nowrap', fontFamily: 'Segoe UI, sans-serif' }}>
            <thead>
              <tr>
                <th rowSpan={2} style={thSticky(C.navy, 0, 85, true)}>Proyecto</th>
                <th rowSpan={2} style={thSticky(C.navy, 85, 185, true)}>Descripción</th>
                <th rowSpan={2} style={thNavy()}>Empresa</th>
                <th rowSpan={2} style={{ ...thNavy(), textAlign: 'right', minWidth: '75px' }}>Contrato s/IVA</th>
                {MESES_CORTOS.map((m, i) => {
                  const pasado = esPasado(i);
                  const actual = esActual(i);
                  return (
                    <th key={m} colSpan={2} style={{
                      ...thNavy(),
                      textAlign: 'center',
                      borderLeft: '1px solid rgba(255,255,255,0.3)',
                      padding: '6px 4px',
                      ...(actual ? { background: C.verde } : {}),
                      ...(pasado ? { opacity: 0.55 } : {}),
                    }}>
                      {m}
                    </th>
                  );
                })}
              </tr>
              <tr>
                {MESES_CORTOS.map((m, i) => {
                  const pasado = esPasado(i);
                  const actual = esActual(i);
                  const extra = actual ? { background: C.verdeOsc } : (pasado ? { opacity: 0.55 } : {});
                  return (
                    <React.Fragment key={m+'sub'}>
                      <th style={{ ...thGris(true),  ...extra }}>₡</th>
                      <th style={{ ...thGris(false), ...extra }}>$</th>
                    </React.Fragment>
                  );
                })}
              </tr>
              {/* Fila totales */}
              <tr style={{ background: C.grisBg, fontWeight: '600' }}>
                <td colSpan={4} style={{ ...tdBase, position: 'sticky', left: 0, background: C.grisBg, textAlign: 'right', padding: '6px 10px', color: C.navy, fontSize: '11px', zIndex: 3 }}>
                  TOTAL PROYECTADO:
                </td>
                {MESES_CORTOS.map((_, mes) => {
                  const pasado = esPasado(mes);
                  const actual = esActual(mes);
                  const tinte = actual ? { background: C.verdeBg }
                              : pasado ? { background: C.grisClaro, opacity: 0.7 }
                              : {};
                  const bordeIzq = actual ? '2px solid ' + C.verde : '1px solid #ccc';
                  return (
                    <React.Fragment key={'tot-'+mes}>
                      <td style={{ ...tdBase, borderLeft: bordeIzq, textAlign: 'right', padding: '6px 6px', color: C.navy, fontSize: '10.5px', ...tinte }}>
                        {totalesMes(mes, 'Colones') ? fmtInt(totalesMes(mes, 'Colones')) : '—'}
                      </td>
                      <td style={{ ...tdBase, textAlign: 'right', padding: '6px 6px', color: C.navy, fontSize: '10.5px', ...tinte }}>
                        {totalesMes(mes, 'Dólares') ? fmtInt(totalesMes(mes, 'Dólares')) : '—'}
                      </td>
                    </React.Fragment>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filasVisibles.length === 0 && (
                <tr><td colSpan={28} style={{ padding: '40px', textAlign: 'center', color: C.gris, fontSize: '13px' }}>
                  No hay filas que coincidan con los filtros.
                </td></tr>
              )}
              {filasVisibles.map((fila, idx) => {
                const esSinItem = fila.tipo === 'sin-item';
                const bgFila = esSinItem ? C.amberBg : '#fff';
                const bgStickyFila = esSinItem ? C.amberBg : '#fff';
                return (
                  <tr key={idx} style={{ background: bgFila, opacity: fila.oculto ? 0.55 : 1 }}>
                    <td style={{ ...tdBase, position: 'sticky', left: 0, background: bgStickyFila, padding: '6px 8px', fontWeight: '500', color: C.navy, borderBottom: '1px solid ' + C.borde, zIndex: 1 }}>
                      {fila.proyecto_id}
                    </td>
                    <td title={`${fila.proyecto_nombre || ''}${fila.descripcion ? ' — ' + fila.descripcion : ''}`}
                        style={{ ...tdBase, position: 'sticky', left: 85, background: bgStickyFila, padding: '6px 8px', borderBottom: '1px solid ' + C.borde, zIndex: 1, maxWidth: '185px', overflow: 'hidden' }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '11.5px', fontWeight: 600, color: C.navy, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.2' }}>
                            {fila.proyecto_nombre || ''}
                          </div>
                          {esSinItem && isAdmin
                            ? <div style={{ marginTop: 3 }}>
                                <SinItemSelector fila={fila} onAsignar={asignarHuerfanas} enCurso={asignandoProyecto === fila.proyecto_id} />
                              </div>
                            : <div style={{ fontSize: '10.5px', color: esSinItem ? C.amberTx : C.gris, fontStyle: esSinItem ? 'italic' : 'normal', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', lineHeight: '1.2', marginTop: 1 }}>
                                {fila.descripcion}
                              </div>}
                        </div>
                        {!esSinItem && isAdmin && (
                          <button onClick={(e) => { e.stopPropagation(); toggleOcultarFila(fila); }}
                            title={fila.oculto ? 'Mostrar esta linea en la matriz' : 'Ocultar esta linea de la matriz'}
                            style={{ flexShrink: 0, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '9.5px', fontWeight: 600, color: fila.oculto ? C.verdeOsc : '#bbb', padding: '0 2px', lineHeight: '1.3' }}>
                            {fila.oculto ? 'mostrar' : 'ocultar'}
                          </button>
                        )}
                      </div>
                    </td>
                    <td style={{ ...tdBase, padding: '6px 8px', color: C.gris, borderBottom: '1px solid ' + C.borde }}>
                      {fila.facturar_con || '—'}
                    </td>
                    <td style={{ ...tdBase, padding: '6px 8px', textAlign: 'right', color: C.navy, borderBottom: '1px solid ' + C.borde }}>
                      {fila.monto_contrato != null ? fmtFull(fila.monto_contrato, fila.moneda_contrato) : '—'}
                    </td>
                    {MESES_CORTOS.map((_, mes) => (
                      <React.Fragment key={'c-'+idx+'-'+mes}>
                        {MONEDAS.map(moneda => {
                          const k = celdaKey(fila.proyecto_id, fila.linea_oferta_id, mes, moneda);
                          const lineas = celdasIndex[k] || [];
                          const bloqueada = celdaBloqueada(lineas);
                          const fk = filaKey(fila.proyecto_id, fila.linea_oferta_id);
                          const esDragOrigen = !!dragFrom && dragFrom.fk === fk && dragFrom.fromMes === mes && dragFrom.moneda === moneda;
                          const esDropValido = !!dragFrom && dragFrom.fk === fk && dragFrom.moneda === moneda && dragFrom.fromMes !== mes;
                          const esDropInvalido = !!dragFrom && !esDropValido && !esDragOrigen;
                          const esHover = dragOverKey === k;
                          return (
                            <CeldaMatriz key={k} lineas={lineas} moneda={moneda} mesEsInicio={moneda === 'Colones'}
                              tipoMes={esActual(mes) ? 'actual' : (esPasado(mes) ? 'pasado' : 'futuro')}
                              onClick={() => abrirCelda(fila, mes, moneda)} clickeable={isAdmin}
                              draggable={isAdmin && lineas.length > 0 && !bloqueada}
                              bloqueada={bloqueada}
                              esDragOrigen={esDragOrigen}
                              esDropValido={esDropValido}
                              esDropInvalido={esDropInvalido}
                              esHover={esHover}
                              onDragStart={(e) => handleDragStart(fila, mes, moneda, e)}
                              onDragEnd={handleDragEnd}
                              onDragOver={(e) => handleDragOver(fila, mes, moneda, e)}
                              onDrop={(e) => handleDrop(fila, mes, moneda, e)}
                              onDragLeave={() => { if (dragOverKey === k) setDragOverKey(null); }}
                            />
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal edición */}
      {celdaActiva && (
        <ModalCelda
          proyecto={celdaActiva.proyecto}
          descripcion={celdaActiva.descripcion}
          mes={celdaActiva.mes}
          moneda={celdaActiva.moneda}
          lineas={celdaActiva.lineas}
          isAdmin={isAdmin}
          onCerrar={() => setCeldaActiva(null)}
          onGuardado={() => { cargar(); setCeldaActiva(null); }}
          onSolicitar={abrirSolicitar}
        />
      )}

      {solicitar && (
        <ModalCobroCliente
          editando={false}
          initialForm={solicitar.initialForm}
          clientes={listas.clientes}
          proyectos={listas.proyectos}
          onClose={() => setSolicitar(null)}
          onSaved={async (cobroId) => {
            try {
              if (cobroId) {
                // La factura puede agrupar hitos de VARIAS lineas de oferta. Hay que
                // enlazar el cobro a todas las filas de proyeccion hermanas, no solo
                // a la que abrio el modal. Criterio: mismo proyecto + mismo mes que la
                // fila disparadora + cuyo item (linea_oferta_id) este en la factura +
                // que sigan pendientes (sin cobro_id).
                const rDet = await axios.get(`${API}/cobros-cliente?id=${cobroId}`);
                const ofertaLineaIds = new Set(
                  (rDet.data.hitos || []).map(h => h.oferta_linea_id).filter(Boolean)
                );
                const base  = data.lineas.find(l => l.id === solicitar.lineaId);
                const mesDe = (f) => (f || '').slice(0, 7); // YYYY-MM, TZ-safe
                const mesBase = base ? mesDe(base.fecha_proyectada) : null;

                const objetivoIds = new Set();
                if (solicitar.lineaId) objetivoIds.add(solicitar.lineaId); // siempre la disparadora
                if (base && mesBase) {
                  for (const l of data.lineas) {
                    if (!l.cobro_id &&
                        l.proyecto_id === base.proyecto_id &&
                        mesDe(l.fecha_proyectada) === mesBase &&
                        ofertaLineaIds.has(l.linea_oferta_id)) {
                      objetivoIds.add(l.id);
                    }
                  }
                }

                await Promise.all([...objetivoIds].map(lid =>
                  axios.put(`${API}/proyeccion-facturacion?id=${lid}`, { cobro_id: cobroId })
                ));
              }
            } catch (e) { /* el cobro se creo; el enlace se puede rehacer por auto-match o manualmente */ }
            setSolicitar(null);
            cargar();
          }}
        />
      )}
    </div>
  );
}

// ── Celda individual ──────────────────────────────────────────────────────────
// Dropdown inline en la fila "Sin item asignado": permite a admin asignar
// bulk todas las huerfanas de un proyecto a un item de oferta (v37.0+).
function SinItemSelector({ fila, onAsignar, enCurso }) {
  const items = fila.itemsDeProyecto || [];
  const n = fila.huerfanas?.length || 0;
  return (
    <select
      defaultValue=""
      disabled={enCurso || items.length === 0}
      onChange={(e) => {
        const id = parseInt(e.target.value);
        if (!id) return;
        const item = items.find(it => it.linea_oferta_id === id);
        const itemDesc = item ? `#${item.item_numero}: ${item.item_descripcion_corta || '(sin desc)'}` : 'item ' + id;
        if (window.confirm(`Asignar ${n} fila(s) huerfana(s) de ${fila.proyecto_id} al ${itemDesc}?\n\nSe enviaran ${n} PUT al API. Puede tomar unos segundos.`)) {
          onAsignar(fila.proyecto_id, id, fila.huerfanas);
        } else {
          e.target.value = '';
        }
      }}
      style={{
        width: '100%',
        fontSize: '11px',
        padding: '3px 4px',
        border: '1px solid ' + (enCurso ? '#ccc' : '#e0b870'),
        borderRadius: '4px',
        background: enCurso ? '#f0f0f0' : '#FAEEDA',
        color: '#854F0B',
        cursor: enCurso ? 'wait' : 'pointer',
        fontFamily: 'Segoe UI, sans-serif'
      }}
    >
      <option value="">{enCurso ? 'Asignando...' : `⚠ Asignar ${n} a item...`}</option>
      {items.map(it => (
        <option key={it.linea_oferta_id} value={it.linea_oferta_id}>
          #{it.item_numero}: {(it.item_descripcion_corta || '(sin desc)').slice(0, 50)}
        </option>
      ))}
    </select>
  );
}

function CeldaMatriz({
  lineas, moneda, mesEsInicio, onClick, clickeable,
  draggable, bloqueada, esDragOrigen, esDropValido, esDropInvalido, esHover,
  onDragStart, onDragEnd, onDragOver, onDrop, onDragLeave,
  tipoMes  // 'pasado' | 'actual' | 'futuro' (default)
}) {
  // Si hay múltiples, calcular estado dominante (prefer Cobrado > Facturado > Pendiente)
  const orden = { Pendiente: 0, Facturado: 1, Cobrado: 2 };
  let estadoDom = 'Pendiente';
  let total = 0;
  for (const l of lineas) {
    total += montoEfectivo(l);
    if (orden[l.estado_calculado] > orden[estadoDom]) estadoDom = l.estado_calculado;
  }
  const est = estiloEstado(estadoDom);
  const vacio = lineas.length === 0;

  // Cursor: candado si bloqueada, grab si draggable, pointer si clickeable
  let cursor = 'default';
  if (bloqueada) cursor = 'not-allowed';
  else if (draggable) cursor = 'grab';
  else if (clickeable) cursor = 'pointer';

  let outline = 'none';
  let outlineOffset = '0px';
  let bgOverride = null;
  let opacity = 1;

  if (esDragOrigen) {
    outline = '2px dashed ' + C.navy;
    outlineOffset = '-2px';
    opacity = 0.6;
  } else if (esHover && esDropValido) {
    outline = '2px solid ' + C.verde;
    outlineOffset = '-2px';
    bgOverride = C.verdeBg;
  } else if (esDropValido) {
    outline = '1px dashed ' + C.verdeOsc;
    outlineOffset = '-1px';
  } else if (esDropInvalido) {
    opacity = 0.35;
  }

  // Mes pasado: muteado (no interfiere con los estados drag, simplemente reduce alfa).
  if (tipoMes === 'pasado') opacity = Math.min(opacity, 0.55);

  const style = {
    ...tdBase,
    padding: '4px 6px',
    textAlign: 'right',
    background: bgOverride || (vacio ? C.grisClaro : est.background),
    color: vacio ? '#bbb' : est.color,
    border: vacio ? '0.5px solid #eee' : '0.5px solid ' + est.borderColor,
    borderLeft: mesEsInicio
      ? (tipoMes === 'actual' ? '2px solid ' + C.verde : '1px solid #ccc')
      : '0.5px solid ' + (vacio ? '#eee' : est.borderColor),
    fontWeight: vacio ? '400' : '500',
    fontSize: '10.5px',
    cursor,
    minWidth: '54px',
    position: 'relative',
    outline,
    outlineOffset,
    opacity,
    transition: 'outline 0.08s, opacity 0.08s, background 0.08s',
    userSelect: 'none',
  };

  const title = bloqueada
    ? 'Celda con línea facturada o cobrada — no arrastrable'
    : (lineas.length > 1 ? `${lineas.length} líneas — clic para editar, arrastrar para mover` :
       (draggable ? 'Clic para editar · arrastrar para mover al mes destino' : ''));

  return (
    <td
      style={style}
      onClick={onClick}
      title={title}
      draggable={draggable}
      onDragStart={draggable ? onDragStart : undefined}
      onDragEnd={draggable ? onDragEnd : undefined}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragLeave={onDragLeave}
    >
      {vacio ? '—' : (
        <>
          {fmtMonto(total, moneda).replace(SIM[moneda], '')}
          {bloqueada && (
            <span style={{ position: 'absolute', top: '1px', left: '2px', fontSize: '8px', color: C.gris }} aria-label="bloqueada">🔒</span>
          )}
          {lineas.length > 1 && (
            <span style={{ position: 'absolute', top: '1px', right: '2px', fontSize: '8px', color: C.navy, background: '#fff', borderRadius: '6px', padding: '0 3px', border: '1px solid #ccc' }}>
              {lineas.length}
            </span>
          )}
        </>
      )}
    </td>
  );
}

// ── Estilos compartidos ───────────────────────────────────────────────────────
const tdBase = {
  borderBottom: '0.5px solid ' + C.borde,
  verticalAlign: 'middle',
};

const thNavy = () => ({
  padding: '8px 10px', textAlign: 'left',
  background: C.navy, color: '#fff', fontWeight: '600', fontSize: '11px',
  position: 'sticky', top: 0, zIndex: 2,
  borderBottom: '1px solid rgba(255,255,255,0.2)',
});

const thSticky = (bg, leftOffset, minWidth, topSticky) => ({
  padding: '8px 10px', textAlign: 'left',
  background: bg, color: '#fff', fontWeight: '600', fontSize: '11px',
  position: 'sticky', left: leftOffset, top: topSticky ? 0 : 'auto', zIndex: 4,
  minWidth: minWidth + 'px',
  borderBottom: '1px solid rgba(255,255,255,0.2)',
});

const thGris = (esInicio) => ({
  padding: '4px 6px', textAlign: 'center',
  background: C.gris, color: '#fff', fontWeight: '500', fontSize: '10px',
  position: 'sticky', top: 33, zIndex: 2,
  borderLeft: esInicio ? '1px solid rgba(255,255,255,0.3)' : 'none',
  borderBottom: '1px solid rgba(255,255,255,0.2)',
});
